import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import {
  endpointForKind,
  type AnyEventItem,
  type EventTimelineKind,
  type PaginatedResponse,
} from './types';

export interface UseEventTimelineArgs {
  kind: EventTimelineKind;
  parentId: string | number;
  limit?: number;
  offset?: number;
  dateFrom?: string;
  dateTo?: string;
  eventTypeCode?: string;
}

export interface UseEventTimelineReturn {
  data: PaginatedResponse<AnyEventItem> | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

const DEFAULT_LIMIT = 20;

export function useEventTimeline(args: UseEventTimelineArgs): UseEventTimelineReturn {
  const {
    kind,
    parentId,
    limit = DEFAULT_LIMIT,
    offset = 0,
    dateFrom,
    dateTo,
    eventTypeCode,
  } = args;

  const params: Record<string, string> = {
    limit: String(limit),
    offset: String(offset),
  };
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;
  if (eventTypeCode) params.eventTypeCode = eventTypeCode;

  const parentIdStr = String(parentId).trim();

  const query = useQuery<PaginatedResponse<AnyEventItem>>({
    queryKey: ['event-timeline', kind, parentIdStr, params],
    queryFn: async () => {
      return api.get<PaginatedResponse<AnyEventItem>>(endpointForKind(kind, parentIdStr), {
        params,
      });
    },
    enabled: parentIdStr.length > 0,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: () => {
      void query.refetch();
    },
  };
}
